import {Component, computed, inject, linkedSignal, output} from '@angular/core';
import {NgOptimizedImage} from '@angular/common';
import {Router} from '@angular/router';
// eslint-disable-next-line no-restricted-imports -- app-text-input takes a FormControl until KAB-2438
import {FormControl} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslatePipe} from '@ngx-translate/core';

import {CurrentTenantStore} from 'src/app/services/tenant/current-tenant.store';
import {IntercomService} from 'src/app/services/ui/intercom.service';
import {TenantOverview} from 'src/app/services/tenant/tenant.service';
import {Permission} from 'src/app/services/auth/permissions.service';
import {SETTINGS_TENANT_URL} from 'src/app/models/home/navigation.model';
import {environment} from 'src/environments/environment';
import {TextInputComponent} from 'src/app/modules/design-system/text-input/text-input.component';
import {normalizeForSearch} from 'src/app/utils/lang.utils';

/** Workspaces listed before « Voir plus ». */
const COLLAPSED_COUNT = 3;

/**
 * Contenu du dropdown « espace » (espace courant, autres espaces, paramètres de l'espace, création),
 * partagé entre le sélecteur du menu principal et le lien « Changer d'espace » de la page de blocage
 * (KAB-2399). L'hôte fournit le `<app-dropdown>` (position) et le ferme sur `closed`.
 */
@Component({
	selector: 'app-tenant-switcher-card',
	imports: [NgOptimizedImage, TranslatePipe, TextInputComponent],
	templateUrl: './tenant-switcher-card.component.html',
	styleUrl: './tenant-switcher-card.component.scss'
})
export class TenantSwitcherCardComponent {

	readonly closed = output<void>();

	private router = inject(Router);
	private intercomService = inject(IntercomService);
	protected readonly tenantStore = inject(CurrentTenantStore);

	protected readonly environment = environment;
	/**
	 * The button opens Settings › Espace. An active workspace always offers it (the section's permissionGuard does the
	 * gating, other sections stay reachable from there); a suspended one offers it only with TENANT_EDIT, the only
	 * users who can open that section (KAB-2399).
	 */
	protected readonly canOpenWorkspaceSettings = computed(() =>
		!this.tenantStore.isSuspended() || this.tenantStore.hasPermission(Permission.TENANT_EDIT));
	protected readonly searchControl = new FormControl<string>('', {nonNullable: true});
	private readonly search = toSignal(this.searchControl.valueChanges, {initialValue: ''});

	/** Every workspace of the account — refreshed along with the current tenant. */
	protected readonly tenants = computed(() => this.tenantStore.tenants() ?? []);

	/** The other workspaces matching the search, by name (the current one is pinned above them). */
	protected readonly others = computed(() => {
		const search = normalizeForSearch(this.search());
		return this.tenants()
			.filter(t => t.configuration.id !== this.tenantStore.currentTenantId())
			.filter(t => !search || normalizeForSearch(t.configuration.name).includes(search))
			.sort((a, b) => a.configuration.name.localeCompare(b.configuration.name));
	});

	/** Collapsed again whenever the search or the workspace changes, so the best matches come first. */
	protected readonly showAll = linkedSignal({source: this.others, computation: () => false});
	protected readonly visibleOthers = computed(() => this.showAll() ? this.others() : this.others().slice(0, COLLAPSED_COUNT));
	protected readonly COLLAPSED_COUNT = COLLAPSED_COUNT;

	protected select(tenant: TenantOverview): void {
		this.closed.emit();
		if (tenant.configuration.id !== this.tenantStore.currentTenantId()) {
			this.tenantStore.changeTenant(tenant.configuration.id);
		}
	}

	protected goToWorkspaceSettings(): void {
		// Each settings section is its own route (router-outlet inside SettingsComponent).
		this.intercomService.hide();
		this.closed.emit();
		this.router.navigate([SETTINGS_TENANT_URL], {queryParamsHandling: 'merge', queryParams: {tenant: this.tenantStore.currentTenantId()}});
	}

	protected createWorkspace(): void {
		this.closed.emit();
		// A page load of its own: the new workspace is the first one of its page, as CurrentTenantStore requires to
		// select it in place (the onboarding goes on after creating it).
		window.location.assign('/new-tenant');
	}
}
